import {
  DatePicker,
  DatePickerProps,
  Input,
  InputNumber,
  InputProps,
  Select,
  SelectProps,
} from "antd";
import { InputNumberProps } from "antd/lib";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useState } from "react";
import {
  DATE_FORMAT,
  END_DATE_FORMAT,
  START_DATE_FORMAT,
} from "../../../core/constants";
import {
  FilterItemProps,
  FilterItemType,
  FilterItemValue,
} from "../../../modules/filters/filters.types";
import { RangePicker, RangePickerProps } from "../../DatePicker/DatePicker";

interface Props {
  value: FilterItemValue;
  type: FilterItemType;
  onChange: (value: FilterItemValue) => void;
  props?: FilterItemProps;
  required?: boolean;
}

const RequiredMark = () => <span style={{ color: "red" }}>*</span>;

export const FilterItem: React.FC<Props> = ({
  value,
  type,
  props,
  onChange,
  required,
}) => {
  dayjs.extend(utc);
  const [initialSelectValue, setInitialSelectValue] =
    useState<FilterItemValue>(undefined);

  const renderFilterComponent = (type: FilterItemType) => {
    switch (type) {
      case FilterItemType.SELECT:
        return (
          <Select
            {...(props as SelectProps)}
            filterOption={(input, option) => {
              const label: any = option?.label;
              if (label) {
                return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }
              return [] as any;
            }}
            value={value ?? initialSelectValue}
            onClear={() => setInitialSelectValue(null)}
            onChange={(value) => onChange(value)}
            allowClear={(props as SelectProps).allowClear ?? true}
            showSearch
          />
        );
      case FilterItemType.DATE:
        return (
          <DatePicker
            {...(props as DatePickerProps)}
            format={DATE_FORMAT}
            value={value as Dayjs | null}
            onChange={(value) => onChange(value)}
            allowClear
          />
        );
      case FilterItemType.DATE_RANGE:
        return (
          <RangePicker
            {...(props as RangePickerProps)}
            format={DATE_FORMAT}
            value={value as any}
            onChange={(value) => {
              if (!value) {
                onChange([null, null]);
                return;
              }

              const startDate = dayjs(
                value?.[0]?.format(START_DATE_FORMAT)
              ).utc();

              const endDate = dayjs(value?.[1]?.format(END_DATE_FORMAT))?.utc();

              onChange([startDate, endDate]);
            }}
            style={{ minWidth: "max-content" }}
            allowClear
          />
        );
      case FilterItemType.NUMBER:
        return (
          <InputNumber
            {...(props as Omit<InputNumberProps, "children">)}
            value={value as number}
            onChange={(value) => onChange(value)}
          />
        );
      case FilterItemType.TEXT:
      default:
        return (
          <Input
            {...(props as InputProps)}
            prefix={required ? <RequiredMark /> : (props as InputProps).prefix}
            value={value as string}
            onChange={(event) => onChange(event.target.value)}
            allowClear
          />
        );
    }
  };

  useEffect(() => {
    if (initialSelectValue !== null && props?.value && !value) {
      setInitialSelectValue(props?.value);
      onChange(props?.value);
    }
  }, [props?.value, value]);

  return renderFilterComponent(type);
};
